import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import { useAddItemToRegistryAttempt } from './useAddItemToRegistryAttempt';
import { useAutoAssociateUserToEvent } from './useAutoAssociateUserToEvent';
import { useEventAssociator } from './useEventAssociator';
import { useEventIdFromContext } from './useEventIdFromContext';
import { EventUserStatus } from '@graphql/generated-base';
import { usePublicShopRootQuery } from '@graphql/queries/publicShop.graphql.generated';
import { useIntlRestrictedEvents } from '@apps/registry/common/hooks/useIntlRestrictedEvents';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useToast } from '@withjoy/joykit';
import { useMemo } from 'react';
import { getShopAddRefusals } from './shopAddRefusals';

export const useShopController = () => {
  const { currentUser, hasInitializedOnce } = useAuth();
  const { eventMemberships, loading: isLoadingEventMemberships } = useEventMemberships(undefined, { includeRegistryEligibility: true });
  const { addToRegistryAttemptInfo, trackAttempt, resetAttempt } = useAddItemToRegistryAttempt();

  const initialEventId = useEventIdFromContext();

  const { eventId, eventPickerChoice, showEventPickerDialog, onSingleMatch, onZeroOrMultipleMatches } = useEventAssociator({ addToRegistryAttemptInfo });

  const { data, loading: isLoadingEventData } = usePublicShopRootQuery({ variables: { eventId: eventId || '', includeEvent: !!eventId }, batchMode: 'fast' });

  useAutoAssociateUserToEvent({
    eventId,
    eventMemberships,
    hasAuthenticatedOnce: hasInitializedOnce && !isLoadingEventMemberships,
    initialEventId,
    onSingleMatch,
    onZeroOrMultipleMatches,
  });

  const { intlRestrictedEventIds, loading: isResolvingIntlEvents } = useIntlRestrictedEvents(eventMemberships);
  const isIntlRestrictedEventSelected = !!eventId && intlRestrictedEventIds.has(eventId);

  // Owner/admin only, matching useAutoAssociateUserToEvent: the hand-off lands on an admin
  // registry route, so a guest membership on an international event is not a destination.
  const intlEventHandles = useMemo(
    () =>
      eventMemberships.flatMap(({ event, status }) =>
        event && (status === EventUserStatus.owner || status === EventUserStatus.admin) && intlRestrictedEventIds.has(event.id) ? [event.website] : [],
      ),
    [eventMemberships, intlRestrictedEventIds],
  );

  const shippingAddress = data?.eventById?.registry?.shippingAddress || null;

  const { toast } = useToast();
  const { tq } = useTranslation('catalogRegistry');
  const intlRestrictedMessage = tq`Your event is outside the US, where the Joy store isn't available yet. You can still add cash funds, and gifts from any other store.`;
  const shippingOutsideUsMessage = tq`Joy store gifts only ship to US addresses. You can still add cash funds, and gifts from any other store.`;
  const handleDeliverableAddRefused = useEventCallback(() => {
    toast(isIntlRestrictedEventSelected ? intlRestrictedMessage : shippingOutsideUsMessage);
    // A refusal consumes the attempt. Deliverable attempts are otherwise only cleared by a
    // successful add, so leaving it set lets useRetryAddCatalogItemAttempt replay the product
    // against the next event the user picks, with no fresh click.
    resetAttempt();
  });

  return {
    currentUser,
    eventId: isLoadingEventData ? undefined : eventId,
    eventType: data?.eventById?.info?.eventType || null,
    handleOnAddItemToRegistryWithoutEvent: trackAttempt,
    ...getShopAddRefusals({ isIntlRestrictedEventSelected, shippingAddress, onAddRefused: handleDeliverableAddRefused }),
    handleJoyStoreAddRefused: handleDeliverableAddRefused,
    isIntlRestrictedEventSelected,
    addToRegistryAttemptInfo,
    removeAddAttemptInfo: resetAttempt,
    showEventPickerDialog,
    eventPickerChoice,
    shippingAddress,
    registryCurrency: data?.eventById?.registry?.currency || null,
    finalizedDate: data?.eventById?.info?.finalizedDate || null,
    registries: data?.eventById?.info?.registry || null,
    orders: data?.eventById?.registry?.orders,
    website: data?.eventById?.website || null,
    finalizedEventDate: data?.eventById?.info?.finalizedDate,
    intlEventHandles,
    isResolvingShopContext: isResolvingIntlEvents || isLoadingEventMemberships || isLoadingEventData,
  } as const;
};
